import { useEffect, useState, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Save, Eye, ZoomIn, ZoomOut, Maximize2, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useToast } from '@/hooks/useToast'
import { templatesApi } from '@/lib/api'
import type { LabelDataField } from '@/lib/api/misc'
import type { TemplateData, LabelElement, ElementType } from './types'
import { TOOLBOX_ITEMS, ELEMENT_DEFAULTS, ELEMENT_TYPE_NAMES } from './types'
import { CanvasElement } from './CanvasElement'
import { PropertiesPanel } from './PropertiesPanel'
import { PreviewModal } from './PreviewModal'
import { labelContextHint, resolveLabelContext } from './labelContext'
import './label-fonts.css'

const SCALE = 4 // px per mm

export function LabelEditorPage() {
  const { templateName } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [loading, setLoading] = useState(true)
  const [template, setTemplate] = useState<TemplateData | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const panRef = useRef(pan)
  panRef.current = pan
  const zoomRef = useRef(zoom)
  zoomRef.current = zoom
  const templateRef = useRef(template)
  templateRef.current = template
  const canvasWrapperRef = useRef<HTMLDivElement>(null)
  const toolDragRef = useRef<{ type: ElementType; startX: number; startY: number; active: boolean } | null>(null)

  const [dragPreview, setDragPreview] = useState<{ type: ElementType; x: number; y: number } | null>(null)

  // Stable refs to avoid re-creating callbacks on every render
  const toastRef = useRef(toast)
  toastRef.current = toast
  const navRef = useRef(navigate)
  navRef.current = navigate

  const isNew = !templateName || templateName === 'None'

  const { data: labelDataFields = [] } = useQuery({
    queryKey: ['label-template-data-fields'],
    queryFn: async () => (await templatesApi.getDataFields()).data as LabelDataField[],
    staleTime: 60_000,
  })

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const doLoad = async () => {
      try {
        if (templateName) {
          const res = await templatesApi.getById(templateName)
          if (!cancelled) {
            const raw = res.data as TemplateData
            const allowed = new Set(['default', 'mono', 'gray', 'rgba'])
            const mode = allowed.has(raw.render_mode || '') ? raw.render_mode! : 'default'
            setTemplate({
              ...raw,
              render_mode: mode,
            })
          }
        } else {
          if (!cancelled) setTemplate({ name: 'Новый шаблон', type: 'km', width: 58, height: 40, elements: [], render_mode: 'default' })
        }
      } catch {
        if (!cancelled) {
          toastRef.current.error('Ошибка загрузки шаблона')
          navRef.current('/admin/settings')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    doLoad()
    return () => { cancelled = true }
  }, [templateName])

  const updateTemplate = (patch: Partial<TemplateData>) => {
    setTemplate(t => t ? ({ ...t, ...patch }) : t)
  }

  const addElement = (type: keyof typeof ELEMENT_DEFAULTS, x: number = 5, y: number = 5) => {
    if (!template) return
    const id = 'e' + Date.now()
    const def = ELEMENT_DEFAULTS[type] ?? {}
    const el: LabelElement = {
      id,
      type: type as ElementType,
      x,
      y,
      width: def.width ?? 10,
      height: def.height ?? 10,
      rotation: def.rotation ?? 0,
      ...def,
    }
    setTemplate(t => t ? ({ ...t, elements: [...t.elements, el] }) : t)
    setSelectedId(id)
  }

  const clientToCanvasMm = useCallback((clientX: number, clientY: number): { x: number; y: number } | null => {
    const canvas = document.getElementById('canvas-container')
    const t = templateRef.current
    if (!canvas || !t) return null
    const rect = canvas.getBoundingClientRect()
    if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) return null
    const scale = SCALE * zoomRef.current
    return {
      x: Math.max(0, +((clientX - rect.left) / scale).toFixed(2)),
      y: Math.max(0, +((clientY - rect.top) / scale).toFixed(2)),
    }
  }, [])

  const handleToolPointerDown = (e: React.PointerEvent<HTMLButtonElement>, type: ElementType) => {
    if (e.button !== 0) return
    e.currentTarget.setPointerCapture(e.pointerId)
    toolDragRef.current = { type, startX: e.clientX, startY: e.clientY, active: false }
  }

  const handleToolPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = toolDragRef.current
    if (!drag) return
    const dx = e.clientX - drag.startX
    const dy = e.clientY - drag.startY
    if (!drag.active && Math.hypot(dx, dy) < 5) return
    drag.active = true
    const pos = clientToCanvasMm(e.clientX, e.clientY)
    setDragPreview(pos ? { type: drag.type, x: pos.x, y: pos.y } : null)
  }

  const handleToolPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = toolDragRef.current
    if (!drag) return
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      // pointer already released
    }
    if (drag.active) {
      const pos = clientToCanvasMm(e.clientX, e.clientY)
      if (pos) addElement(drag.type, pos.x, pos.y)
    } else {
      addElement(drag.type)
    }
    toolDragRef.current = null
    setDragPreview(null)
  }

  const handleToolPointerCancel = () => {
    toolDragRef.current = null
    setDragPreview(null)
  }

  const onMove = useCallback((id: string, x: number, y: number) => {
    setTemplate(t => {
      if (!t) return t
      return { ...t, elements: t.elements.map(el => el.id === id ? { ...el, x, y } : el) }
    })
  }, [])

  const onResize = useCallback((id: string, patch: { x: number; y: number; width: number; height: number }) => {
    setTemplate(t => {
      if (!t) return t
      return {
        ...t,
        elements: t.elements.map(el => el.id === id ? { ...el, ...patch } : el),
      }
    })
  }, [])

  const updateElementProp = (id: string, prop: keyof LabelElement, value: unknown) => {
    setTemplate(t => {
      if (!t) return t
      return { ...t, elements: t.elements.map(el => el.id === id ? ({ ...el, [prop]: value } as LabelElement) : el) }
    })
  }

  const deleteSelected = () => {
    if (!template || !selectedId) return
    setTemplate({ ...template, elements: template.elements.filter(e => e.id !== selectedId) })
    setSelectedId(null)
  }

  const rotateSelected = (delta: number) => {
    if (!template || !selectedId) return
    setTemplate(t => {
      if (!t) return t
      return { ...t, elements: t.elements.map(el => el.id === selectedId ? ({ ...el, rotation: ((el.rotation || 0) + delta + 360) % 360 }) : el) }
    })
  }

  const saveTemplate = async () => {
    if (!template) return
    if (!template.name || template.name.trim() === '') { toast.error('Введите название шаблона'); return }
    try {
      if (isNew) {
        const res = await templatesApi.create(template as any)
        const data = res.data as any
        const target = data.filename ?? template.name
        toast.success('Шаблон создан')
        navigate(`/admin/label-editor/${encodeURIComponent(target)}`)
      } else {
        await templatesApi.save(templateName!, template as any)
        toast.success('Шаблон сохранён')
      }
    } catch {
      toast.error('Ошибка сохранения шаблона')
    }
  }

  const handleSelect = useCallback((id: string) => setSelectedId(id), [])
  const handleDeselect = useCallback(() => setSelectedId(null), [])
  const openPreview = useCallback(() => setIsPreviewOpen(true), [])
  const closePreview = useCallback(() => setIsPreviewOpen(false), [])
  const goBack = useCallback(() => navigate('/admin/settings'), [navigate])
  const getTemplate = useCallback(() => template!, [template])

  const ZOOM_MIN = 0.25
  const ZOOM_MAX = 5
  const ZOOM_STEP = 0.25

  const zoomIn = useCallback(() => setZoom(z => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2))), [])
  const zoomOut = useCallback(() => setZoom(z => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2))), [])
  const zoomReset = useCallback(() => setZoom(1), [])
  const panReset = useCallback(() => {
    setPan({ x: 0, y: 0 })
    panRef.current = { x: 0, y: 0 }
    const wrapper = canvasWrapperRef.current
    if (wrapper) {
      wrapper.scrollLeft = 0
      wrapper.scrollTop = 0
    }
  }, [])

  useEffect(() => {
    const wrapper = canvasWrapperRef.current
    if (!wrapper) return
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()
      setZoom(z => {
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP
        return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, +(z + delta).toFixed(2)))
      })
    }

    const handleMiddleDown = (e: MouseEvent) => {
      if (e.button !== 1) return
      e.preventDefault()
      wrapper.style.cursor = 'grabbing'
      const startX = e.clientX
      const startY = e.clientY
      const initPanX = panRef.current.x
      const initPanY = panRef.current.y

      const handleMiddleMove = (me: MouseEvent) => {
        const newPan = { x: initPanX + (me.clientX - startX), y: initPanY + (me.clientY - startY) }
        setPan(newPan)
        panRef.current = newPan
      }
      const handleMiddleUp = () => {
        wrapper.style.cursor = ''
        document.removeEventListener('mousemove', handleMiddleMove)
        document.removeEventListener('mouseup', handleMiddleUp)
      }
      document.addEventListener('mousemove', handleMiddleMove)
      document.addEventListener('mouseup', handleMiddleUp)
    }

    wrapper.addEventListener('wheel', handleWheel, { passive: false })
    wrapper.addEventListener('mousedown', handleMiddleDown)
    return () => {
      wrapper.removeEventListener('wheel', handleWheel)
      wrapper.removeEventListener('mousedown', handleMiddleDown)
    }
  }, [loading])

  if (loading || !template) return <LoadingSpinner className="mt-20" />

  const effectiveScale = SCALE * zoom
  const labelContext = resolveLabelContext(template)
  const contextHint = labelContextHint(labelContext)

  const fieldSourceHint = (field: LabelDataField) => {
    if (labelContext && field.sources?.[labelContext]) return field.sources[labelContext]
    return field.source
  }

  const canvasStyle: React.CSSProperties = {
    width: template.width * effectiveScale,
    height: template.height * effectiveScale,
    position: 'relative',
    background: 'white',
    border: '2px solid #9ca3af',
    zIndex: 0,
    transition: 'width 0.15s ease, height 0.15s ease',
  }

  return (
    <div className="editor-container">
      <div className="editor-header flex items-center justify-between">
        <div className="flex-1 pr-4">
          <Input 
          value={template.name} 
          onChange={e => updateTemplate({ name: e.target.value })} 
          className="mb-2 border-transparent focus:border-gray-400 focus:ring-0" 
          />
          <div className="flex items-center gap-2 flex-wrap">
            <label className="font-medium mr-2 shrink-0">Размер этикетки:</label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={String(template.width)}
                onChange={e => updateTemplate({ width: parseInt(e.target.value || '0', 10) })}
                className="w-20 border-transparent focus:border-gray-400 focus:ring-0"
              />
              <span>×</span>
              <Input
                type="number"
                value={String(template.height)}
                onChange={e => updateTemplate({ height: parseInt(e.target.value || '0', 10) })}
                className="w-20 border-transparent focus:border-gray-400 focus:ring-0"
              />
            </div>
            <div className="flex items-center gap-1 ml-2">
              <Button variant="ghost" size="sm" onClick={zoomOut} title="Уменьшить"><ZoomOut className="w-4 h-4" /></Button>
              <button
                type="button"
                onClick={zoomReset}
                className="text-xs font-mono px-2 py-0.5 rounded border border-gray-300 hover:bg-gray-100 min-w-[56px] text-center h-8"
                title="Сбросить масштаб"
              >
                {Math.round(zoom * 100)}%
              </button>
              <Button variant="ghost" size="sm" onClick={zoomIn} title="Увеличить"><ZoomIn className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={panReset} title="Сбросить позицию"><RefreshCw className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={zoomReset} title="По умолчанию"><Maximize2 className="w-4 h-4" /></Button>
            </div>
            <div className="ml-auto">
              <Button variant="secondary" size="sm" onClick={() => { /* apply happens automatically */ }}>✓ Применить</Button>
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5">
            <span className="text-xs font-semibold text-amber-900 shrink-0">Режим рендера PNG:</span>
            <div className="flex items-center border border-amber-400 rounded-md overflow-hidden bg-white">
              {([
                { id: 'default' as const, label: 'Общий', title: 'Из Настроек → Режим печати' },
                { id: 'mono' as const, label: 'Mono', title: '1-бит ЧБ' },
                { id: 'gray' as const, label: 'Gray', title: 'ЧБ с серыми оттенками' },
                { id: 'rgba' as const, label: 'RGBA', title: 'Полный RGBA' },
              ]).map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  className={`text-xs px-3 h-8 ${(template.render_mode || 'default') === opt.id ? 'bg-amber-700 text-white' : 'text-gray-800 hover:bg-amber-100'}`}
                  title={opt.title}
                  onClick={() => updateTemplate({ render_mode: opt.id })}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <span className="text-[11px] text-amber-800/80">шаблон важнее общего в Настройках</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={openPreview}><Eye className="w-4 h-4" /> Предпросмотр</Button>
          <Button variant="primary" size="sm" onClick={saveTemplate}><Save className="w-4 h-4" /> Сохранить</Button>
          <Button variant="secondary" size="sm" onClick={goBack}>Отмена</Button>
        </div>
      </div>

      <div className="editor-main grid grid-cols-[220px_1fr_320px] gap-0 mt-4 h-[calc(100vh-11rem)] min-h-[480px]">
        <div className="editor-sidebar left p-4 overflow-y-auto min-h-0 border-r border-gray-200">
          <h3 className="text-xs font-semibold text-gray-600 mb-3">Элементы</h3>
          <div className="flex flex-col gap-2">
            {TOOLBOX_ITEMS.map(it => (
              <button
                key={it.type}
                type="button"
                className="tool-btn text-left touch-none select-none"
                onPointerDown={e => handleToolPointerDown(e, it.type)}
                onPointerMove={handleToolPointerMove}
                onPointerUp={handleToolPointerUp}
                onPointerCancel={handleToolPointerCancel}
              >
                {it.label}
              </button>
            ))}
          </div>
          <h3 className="text-xs font-semibold text-gray-600 mt-6 mb-2">Поля данных</h3>
          <p className="text-xs text-gray-500 mb-2">
            Клик по полю — привязать к выбранному элементу (текст/штрихкод).
          </p>
          {contextHint && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-2">
              {contextHint}
            </p>
          )}
          <div className="flex flex-col gap-1">
            {labelDataFields.map(field => (
              <button
                type="button"
                key={field.key}
                className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-left hover:border-blue-400 hover:bg-blue-50 disabled:opacity-50 disabled:hover:bg-gray-50 disabled:hover:border-gray-200"
                title={selectedId ? `Привязать ${field.key}` : fieldSourceHint(field)}
                disabled={!selectedId}
                onClick={() => {
                  if (!selectedId) return
                  const el = template?.elements.find(e => e.id === selectedId)
                  if (!el) return
                  if (el.type === 'text') {
                    updateElementProp(selectedId, 'field', field.key)
                    if (!el.label) updateElementProp(selectedId, 'label', '')
                  } else if (
                    el.type === 'datamatrix' ||
                    el.type === 'datamatrix_ka' ||
                    el.type === 'gs1_128' ||
                    el.type === 'ean13'
                  ) {
                    updateElementProp(selectedId, 'field', field.key)
                  }
                }}
              >
                <span className="block text-sm text-gray-800">{field.label}</span>
                <span className="block text-[11px] text-gray-500 font-mono">{`{${field.key}}`}</span>
                {field.sources && labelContext && field.sources[labelContext] && (
                  <span className="block text-[10px] text-gray-400 mt-0.5 leading-snug">
                    {field.sources[labelContext]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col min-h-0 min-w-0 h-full">
          <div
            className="editor-canvas-wrapper flex-1 min-h-0 flex items-center justify-center p-8 overflow-auto"
            ref={canvasWrapperRef}
            style={{ border: '4px solid #e5e7eb' }}
          >
            <div
              id="canvas-container"
              style={{ ...canvasStyle, transform: `translate(${pan.x}px, ${pan.y}px)` }}
              onClick={handleDeselect}
            >
              {template.elements.map(el => (
                <CanvasElement
                  key={el.id}
                  element={el}
                  scale={effectiveScale}
                  selected={selectedId === el.id}
                  onSelect={handleSelect}
                  onMove={onMove}
                  onResize={onResize}
                  templateWidth={template.width}
                  templateHeight={template.height}
                />
              ))}
              {dragPreview && (() => {
                const def = ELEMENT_DEFAULTS[dragPreview.type]
                const w = (def.width || 10) * effectiveScale
                const h = (def.height || 10) * effectiveScale
                return (
                  <div
                    className="flex items-center justify-center pointer-events-none"
                    style={{
                      position: 'absolute',
                      left: dragPreview.x * effectiveScale,
                      top: dragPreview.y * effectiveScale,
                      width: w,
                      height: h,
                      opacity: 0.5,
                      border: '2px dashed #3b82f6',
                      background: 'rgba(59,130,246,0.12)',
                    }}
                  >
                    <span className="text-xs text-blue-600 font-medium">
                      {ELEMENT_TYPE_NAMES[dragPreview.type]}
                    </span>
                  </div>
                )
              })()}
            </div>
          </div>
        </div>

        <div className="editor-sidebar right p-4 overflow-y-auto min-h-0 border-l border-gray-200">
          <h3 className="text-xs font-semibold text-gray-600 mb-3">Свойства</h3>
          <PropertiesPanel
            element={template.elements.find(e => e.id === selectedId) ?? null}
            onUpdate={(prop, value) => { if (selectedId) updateElementProp(selectedId, prop as any, value) }}
            onDelete={deleteSelected}
            onRotate={rotateSelected}
          />
        </div>
      </div>

      <PreviewModal isOpen={isPreviewOpen} onClose={closePreview} getTemplate={getTemplate} />
    </div>
  )
}

export default LabelEditorPage
